import { LibraryFilters } from './library';
import { Location, Referrer } from './ui';

// This is pulled from native expo-router for parity with
// mobile and should probably be defined elsewhere, or
// differently
export type UnknownOutputParams = Record<string, string | string[]>;

/* Analytic Event interfaces */
export interface ClickThroughProps {
  target: string;
  referrer: Referrer;
  section: string;
  resourceId: string;
}

interface EventBase {
  location?: Location;
  referrer?: Referrer;
}

export interface MemberManagementEventProps extends EventBase {
  organizationMemberIds: number[];
}

export interface CarouselScrollEventProps extends EventBase {
  direction: string;
}

export interface CarouselSwitchTabEventProps extends EventBase {
  tabName: string;
}

export interface ChapterEventProps extends EventBase {
  chapterId: string;
}

export interface EpisodeEventProps extends EventBase {
  episodeId: string;
}

export type AnalyticEventProps = ChapterEventProps & EpisodeEventProps;

export interface LearningPathEventProps extends EventBase {
  learningPathId: number;
}

export interface LearningPathCardClickedEventProps
  extends LearningPathEventProps {
  episodesCompleted: number;
  isCompleted: boolean;
  topic: string;
  isRecommended: boolean;
}

export interface LearningPathSidebarItemClickedEventProps {
  path: string;
  label: string;
}

export interface LearningPathsPageViewedEventProps {
  inProgressPathsCount: number;
  recommendedPathsCount: number;
  viewMode: string;
  pathname: string;
}

export interface LearningPathViewModeToggledEventProps {
  viewMode: string;
}

export interface LearningPathViewAllClickedEventProps {
  section: string;
}

export interface FilterEventProps {
  filters: LibraryFilters;
  categoriesFilter?: (string | undefined)[];
  fieldsOfStudyFilter?: (string | undefined)[];
}

export interface ProductFeedbackEventProps {
  productFeedbackForm: Record<string, string | boolean | undefined>;
  isDismissed?: boolean;
  referrer: Referrer;
}

export interface DateRangeEventProps {
  previousStartDate: string | undefined;
  previousEndDate: string | undefined;
  newStartDate: string | undefined;
  newEndDate: string | undefined;
}

/* Analytic Event types */
export type BookmarkEventProps = EpisodeEventProps & { value: boolean };

export type EpisodeFeedbackEventProps = AnalyticEventProps & {
  episodeRating: number;
  chapterProgress: number;
  episodePlayedDuration: number;
  textFeedback: string | null;
  ratingReasons: string | null;
};

export type EpisodeEvaluationEventProps = EpisodeEventProps & {
  answers: Record<string, number | undefined>;
};

export type EpisodeDetailSwitchTabProps = EpisodeEventProps & {
  tabName: string;
};

export type DownloadEventProps = EpisodeEventProps & { downloading: boolean };

export type ResourceViewEventProps = EpisodeEventProps & {
  resourceName: string;
};

export type PauseEventProps = AnalyticEventProps & { pausing?: boolean };

export type SeekEventProps = AnalyticEventProps & {
  alreadyListened: boolean;
  seekStart: number;
  seekLength: number;
  type?: string;
};

export type VolumeEventProps = AnalyticEventProps & { val: number };

export type PlaybackSpeedChangedEventProps = AnalyticEventProps & {
  speed: number;
};

export type QuizAttemptEventProps = ChapterEventProps & {
  quizId: string;
  answer: string;
  pass: boolean | undefined;
};

export type QuizViewEventProps = ChapterEventProps & {
  quizId: string;
  question: string | undefined;
  isAvailable: boolean;
  isAnswered: boolean;
};

export type AssessmentEventAttemptProps = EpisodeEventProps & {
  assessmentId: string;
  answer: (number | null)[];
  score: number;
  pass: boolean;
};

export type SkipEventProps = AnalyticEventProps & { direction: string };

export type VideoPlayerFullScreenEventProps = AnalyticEventProps & {
  isFullScreen: boolean;
};

export type ShowModalProps = {
  name: string;
  location: Location;
};

export const CsvVariants = {
  LISTENER_DISTRIBUTION: 'listener_distribution',
  LEARNING_TREND: 'learning_trend',
} as const;
